import { AiChatEvent } from '@qtek/shared/models';

/**
 * Wire shape of a ChatEvent as it arrives over the WS — v2 (`chat` / `source` /
 * `version`) or the tolerated v1 (`chatId` / `src` / `ts`). Normalized to
 * {@link AiChatEvent} by {@link normalizeChatEvent}.
 */
export interface RawChatEvent {
  chat?: string;
  chatId?: string;
  source?: string;
  src?: string;
  version?: string | number;
  ts?: string | number;
  data?: string;
  documents?: string[];
  documentIds?: AiChatEvent['documentIds'];
  toolsUsed?: AiChatEvent['toolsUsed'];
  actionCards?: AiChatEvent['actionCards'];
  cancelled?: boolean | null;
}

/**
 * SD-2537: normalize a v2 `ChatEvent` (`{ chat, source, version, data, toolsUsed, actionCards }`)
 * into the internal {@link AiChatEvent} shape (`{ chatId, ts, src, data, toolsUsed, actionCards }`).
 * Tolerates the v1 shape too (`chatId` / `ts` / `src`). `version` may arrive as epoch millis or an
 * ISO instant; both resolve to the same millis the `chatStream` `ts` uses, so the streamed bubble
 * reconciles.
 *
 * SD-2866: `actionCards` must be carried across. Every field is copied by name, and this one was
 * missed when SD-2865 added it, so a card shown live on the streaming bubble vanished the moment
 * the committed event replaced it — invisible only while the model still echoed cards into `data`.
 */
export function normalizeChatEvent(
  raw: RawChatEvent | null | undefined
): AiChatEvent | null {
  if (!raw) return null;
  const chatId = raw.chat ?? raw.chatId;
  if (!chatId) return null;
  const versionSource = raw.version ?? raw.ts;
  let ts: number;
  if (typeof versionSource === 'number') {
    ts = versionSource;
  } else {
    const parsed = Date.parse(String(versionSource));
    ts = Number.isNaN(parsed) ? Date.now() : parsed;
  }
  const src: 'user' | 'bot' =
    String(raw.source ?? raw.src ?? '').toLowerCase() === 'bot'
      ? 'bot'
      : 'user';
  return {
    chatId,
    ts,
    src,
    data: raw.data ?? '',
    // v2 field is `documents` (document ids); tolerate v1's `documentIds`.
    documentIds: raw.documents ?? raw.documentIds,
    toolsUsed: raw.toolsUsed,
    actionCards: raw.actionCards,
    // SD-2819: set by the BE on a reply whose turn was stopped (SD-2823). Dropping it here made a
    // reloaded stopped reply look like an ordinary reply that ends mid-sentence.
    ...(raw.cancelled === true ? { cancelled: true } : {}),
  };
}
